import { connectRealtime } from '@/lib/api-client';
import type { Socket } from 'socket.io-client';
import { tokenStore } from './api';

let socket: Socket | null = null;
let connectedWith: string | null = null;

/**
 * Whether this tab has ever had a working socket.
 *
 * The difference between "connected" and "connected *again*" is the whole
 * point: a first connect has nothing to catch up on — the screen's own load is
 * running beside it — and every later one sits on top of a gap. Module-level
 * rather than per-socket, so a socket rebuilt after the guest's credential
 * changed still counts as a reconnect for the screens that were watching the
 * old one.
 */
let everConnected = false;
const reconnectListeners = new Set<() => void>();

/**
 * Fires on every connect *after* the first — a Wi-Fi blip healed, a backend
 * restart survived, a phone whose radio the OS put to sleep waking up.
 *
 * A screen that only ever repaints from an event drifts silently across one of
 * these: the socket was down, the kitchen was not, and every event fired in
 * that window is simply gone — Socket.IO restores room membership on reconnect
 * and replays nothing. The screen carries on looking live while showing a state
 * that stopped being true at the moment of the blip, which is worse than
 * showing a spinner. See `@/lib/resync`, which is what listens to this.
 */
export function onRealtimeReconnect(listener: () => void): () => void {
  reconnectListeners.add(listener);
  return () => {
    reconnectListeners.delete(listener);
  };
}

/**
 * One shared socket per tab — components subscribe, they never reconnect.
 *
 * The gateway authenticates the handshake, so the socket is rebuilt
 * whenever the credential changes. A guest opens its session *after* the first
 * render, so a socket created before the scan would otherwise stay anonymous
 * and be disconnected as unauthorized.
 */
export function getSocket(rooms: string[] = []): Socket {
  const token = (tokenStore.get() as { accessToken?: string } | null)?.accessToken ?? null;

  if (socket && connectedWith !== token) disconnectSocket();

  if (!socket) {
    connectedWith = token;
    socket = connectRealtime({
      baseURL: import.meta.env.VITE_REALTIME_URL ?? 'http://localhost:4000',
      token: token ?? undefined,
      rooms,
    });
    // A guest may only join its own session and its own orders' rooms; the gateway
    // answers a refusal rather than dropping it silently, so surface it.
    socket.on('join:denied', (denied) => console.warn('realtime rooms denied', denied));

    socket.on('connect', () => {
      if (everConnected) for (const listener of [...reconnectListeners]) listener();
      everConnected = true;
    });
  } else if (rooms.length) {
    socket.emit('join', { rooms });
  }
  return socket;
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
  connectedWith = null;
  // Back to "never connected". This is the guest leaving the table or scanning
  // another one, so the next socket's first connect belongs to a screen that is
  // loading from scratch — treating it as a reconnect would be a duplicate read
  // on every scan.
  everConnected = false;
}
